import { useState, useRef, useEffect } from 'react'
import {
  Building2,
  Trash2,
  Copy,
  Check,
  Loader2,
  Upload,
  ImageIcon,
  X,
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { useAuthStore } from '@/lib/auth/authStore'

// ── Types ────────────────────────────────────────────────────────────────────
interface Organization {
  id: string
  tenant_id: string
  name: string
  description: string
  active: boolean
  logo_url?: string | null
  created_at: string
}

// ── Skeleton / Error States ──────────────────────────────────────────────────
function OrganizationSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-1" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

function OrganizationErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16">
        <Building2 className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to Load Organization</h3>
        <p className="text-muted-foreground text-center max-w-md mb-4">
          Unable to retrieve organization details. Please try again.
        </p>
        <Button onClick={onRetry}>Retry</Button>
      </CardContent>
    </Card>
  )
}

// ── Logo constants ───────────────────────────────────────────────────────────
const ACCEPTED_LOGO_TYPES = '.jpg,.jpeg,.png,.gif,.svg,.webp'
const MAX_LOGO_SIZE_MB = 5
const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024

// ── Main Component ───────────────────────────────────────────────────────────
export default function OrganizationPage() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const logoInputRef = useRef<HTMLInputElement>(null)

  // -- Org data fetch --------------------------------------------------------
  const orgId = user?.organization_id ?? ''

  const {
    data: organization,
    isLoading,
    isError,
    refetch,
  } = useQuery<Organization>({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/api/auth/organizations/${orgId}`)
      return data
    },
    enabled: !!orgId,
  })

  // -- Form state ------------------------------------------------------------
  const [orgData, setOrgData] = useState({ name: '', description: '' })
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [copiedId, setCopiedId] = useState(false)
  const [copiedTenant, setCopiedTenant] = useState(false)

  // Sync form when org loads
  useEffect(() => {
    if (organization) {
      setOrgData({
        name: organization.name ?? '',
        description: organization.description ?? '',
      })
    }
  }, [organization])

  // -- Logo state ------------------------------------------------------------
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploadedLogoId, setUploadedLogoId] = useState<number | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [isRemovingLogo, setIsRemovingLogo] = useState(false)

  // Authenticated image fetch: browser <img> tags don't send JWT, so we
  // fetch via apiClient and create a blob URL for display.
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null)

  // Fetch logo blob whenever uploadedLogoId changes
  useEffect(() => {
    if (!uploadedLogoId) {
      // Revoke old blob to avoid memory leaks
      if (logoBlobUrl) URL.revokeObjectURL(logoBlobUrl)
      setLogoBlobUrl(null)
      return
    }

    let cancelled = false
    const fetchBlob = async () => {
      try {
        const resp = await apiClient.get(`/api/attachments/${uploadedLogoId}/download`, {
          responseType: 'blob',
        })
        if (cancelled) return
        const url = URL.createObjectURL(resp.data)
        setLogoBlobUrl(url)
      } catch {
        // If download fails, fall back to nothing — preview may still be visible
      }
    }
    fetchBlob()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedLogoId])

  // Also try to load from persisted logo_url (if org has a logo attachment ID stored)
  const persistedLogoUrl = organization?.logo_url || null

  useEffect(() => {
    if (!persistedLogoUrl || uploadedLogoId || logoBlobUrl) return

    // If the persisted URL is a relative API path, fetch it authenticated
    if (persistedLogoUrl.startsWith('/api/')) {
      let cancelled = false
      const fetchPersistedBlob = async () => {
        try {
          const resp = await apiClient.get(persistedLogoUrl, {
            responseType: 'blob',
          })
          if (cancelled) return
          const url = URL.createObjectURL(resp.data)
          setLogoBlobUrl(url)
        } catch {
          // Logo URL invalid or deleted — ignore
        }
      }
      fetchPersistedBlob()
      return () => { cancelled = true }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedLogoUrl])

  const displayLogoSrc = logoPreview ?? logoBlobUrl

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (logoBlobUrl) URL.revokeObjectURL(logoBlobUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -- Mutations -------------------------------------------------------------
  const updateOrganization = useMutation({
    mutationFn: async (payload: { name: string; description: string; logo_url?: string | null }) => {
      const { data } = await apiClient.put(
        `/api/auth/organizations/${orgId}`,
        payload,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] })
      toast.success('Organization updated')
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'Failed to update organization')
    },
  })

  const deleteOrganization = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/api/auth/organizations/${orgId}`)
    },
  })

  // -- Handlers --------------------------------------------------------------
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgData.name.trim()) {
      toast.error('Organization name is required')
      return
    }
    updateOrganization.mutate(orgData)
  }

  const handleDelete = async () => {
    try {
      await deleteOrganization.mutateAsync()
      toast.success('Organization deleted. You will be signed out.')
      window.location.href = '/login'
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete organization')
    }
  }

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, GIF, SVG, or WebP)')
      return
    }

    // Validate size
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      toast.error(`Logo must be under ${MAX_LOGO_SIZE_MB}MB`)
      return
    }

    // Show local preview immediately
    const reader = new FileReader()
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)

    setIsUploadingLogo(true)
    try {
      // Upload via the files endpoint
      const formData = new FormData()
      formData.append('file', file)
      formData.append('title', 'Organization Logo')
      formData.append('description', `Logo for ${organization!.name}`)

      const { data: uploaded } = await apiClient.post('/api/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const newLogoId = uploaded.id
      const newLogoUrl = `/api/attachments/${newLogoId}/download`

      // Persist the logo URL on the organization
      try {
        await apiClient.put(`/api/auth/organizations/${orgId}`, {
          ...orgData,
          logo_url: newLogoUrl,
        })
        queryClient.invalidateQueries({ queryKey: ['organization', orgId] })
      } catch {
        // If the org doesn't support logo_url yet, that's fine — the upload still succeeded
      }

      // If there was a previously uploaded logo (this session), clean it up
      if (uploadedLogoId) {
        try {
          await apiClient.delete(`/api/attachments/${uploadedLogoId}`)
        } catch {
          // Non-critical — old file cleanup is best-effort
        }
      }

      setUploadedLogoId(newLogoId)
      setLogoPreview(null) // Clear preview; real URL is now set
      toast.success('Logo uploaded successfully')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo')
      setLogoPreview(null)
    } finally {
      setIsUploadingLogo(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  const handleRemoveLogo = async () => {
    setIsRemovingLogo(true)
    try {
      // Delete the file if we have an ID
      const idToDelete = uploadedLogoId
      if (idToDelete) {
        try {
          await apiClient.delete(`/api/attachments/${idToDelete}`)
        } catch {
          // Best-effort cleanup
        }
      }

      // Clear logo_url on the organization
      try {
        await apiClient.put(`/api/auth/organizations/${orgId}`, {
          ...orgData,
          logo_url: null,
        })
        queryClient.invalidateQueries({ queryKey: ['organization', orgId] })
      } catch {
        // If org doesn't support logo_url, just clear local state
      }

      setUploadedLogoId(null)
      setLogoPreview(null)
      toast.success('Logo removed')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to remove logo')
    } finally {
      setIsRemovingLogo(false)
    }
  }

  const copyToClipboard = async (
    text: string,
    setter: (v: boolean) => void,
  ) => {
    try {
      await navigator.clipboard.writeText(text)
      setter(true)
      setTimeout(() => setter(false), 2000)
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  // -- Render ----------------------------------------------------------------
  if (isLoading) return <OrganizationSkeleton />
  if (isError) return <OrganizationErrorState onRetry={() => refetch()} />

  if (!organization) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Organization Found</h3>
          <p className="text-muted-foreground text-center max-w-md">
            Your account is not associated with an organization. Contact your
            administrator for assistance.
          </p>
        </CardContent>
      </Card>
    )
  }

  const deleteEnabled =
    deleteConfirmText === organization.name && !deleteOrganization.isPending

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* Organization Details Card                                           */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organization Details
          </CardTitle>
          <CardDescription>
            Update your organization's name and description
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="org-name">Organization Name *</Label>
              <Input
                id="org-name"
                value={orgData.name}
                onChange={(e) =>
                  setOrgData({ ...orgData, name: e.target.value })
                }
                placeholder="Acme Corporation"
              />
            </div>

            <div>
              <Label htmlFor="org-description">Description</Label>
              <Textarea
                id="org-description"
                value={orgData.description}
                onChange={(e) =>
                  setOrgData({ ...orgData, description: e.target.value })
                }
                placeholder="Brief description of your organization…"
                rows={4}
              />
            </div>

            <Separator />

            {/* Read-only metadata */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Organization ID</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={organization.id}
                    readOnly
                    className="bg-muted font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() =>
                      copyToClipboard(organization.id, setCopiedId)
                    }
                  >
                    {copiedId ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Used for API integrations
                </p>
              </div>

              <div>
                <Label>Tenant ID</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={organization.tenant_id}
                    readOnly
                    className="bg-muted font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() =>
                      copyToClipboard(organization.tenant_id, setCopiedTenant)
                    }
                  >
                    {copiedTenant ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Multi-tenant isolation identifier
                </p>
              </div>

              <div>
                <Label>Status</Label>
                <div className="mt-1">
                  <Badge
                    variant={organization.active ? 'default' : 'secondary'}
                    className="text-sm"
                  >
                    {organization.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Created{' '}
                  {new Date(organization.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={updateOrganization.isPending}
              >
                {updateOrganization.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Logo / Branding Card                                                */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Organization Logo
          </CardTitle>
          <CardDescription>
            Upload your organization's logo. Displayed in reports, dashboards,
            and exported documents. Recommended size: 256×256px or larger.
            Accepted formats: PNG, JPG, GIF, SVG, WebP (max {MAX_LOGO_SIZE_MB}MB).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Hidden file input */}
          <input
            ref={logoInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES}
            className="hidden"
            onChange={handleLogoSelect}
          />

          {displayLogoSrc ? (
            /* ── Has logo ── */
            <div className="flex items-start gap-6">
              <div className="relative group">
                <div className="h-32 w-32 rounded-lg border bg-muted flex items-center justify-center overflow-hidden">
                  <img
                    src={displayLogoSrc}
                    alt={`${organization.name} logo`}
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </div>
                {/* Overlay remove button on hover */}
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  disabled={isRemovingLogo}
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  title="Remove logo"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="space-y-3 pt-1">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={isUploadingLogo}
                  >
                    {isUploadingLogo ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Replace Logo
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    disabled={isRemovingLogo}
                    className="text-destructive hover:text-destructive"
                  >
                    {isRemovingLogo ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="mr-2 h-4 w-4" />
                    )}
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* ── No logo ── */
            <div
              className="flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
              onClick={() => logoInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  logoInputRef.current?.click()
                }
              }}
            >
              {isUploadingLogo ? (
                <Loader2 className="h-10 w-10 text-muted-foreground mb-3 animate-spin" />
              ) : (
                <ImageIcon className="h-10 w-10 text-muted-foreground mb-3" />
              )}
              <p className="text-sm font-medium">
                {isUploadingLogo ? 'Uploading…' : 'Click to upload a logo'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, GIF, SVG, or WebP up to {MAX_LOGO_SIZE_MB}MB
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Danger Zone                                                         */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Irreversible actions that affect your entire organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border border-destructive/50 rounded-lg bg-destructive/5">
            <div>
              <h3 className="font-semibold text-destructive">
                Delete Organization
              </h3>
              <p className="text-sm text-muted-foreground">
                Permanently delete this organization and all associated data.
                This action cascades to all members.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation Dialog                                          */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. All members will
              lose access and all associated data will be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">
              Type <span className="font-mono font-bold">{organization.name}</span>{' '}
              to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={organization.name}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false)
                setDeleteConfirmText('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!deleteEnabled}
              onClick={handleDelete}
            >
              {deleteOrganization.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
